import React, { useMemo } from 'react';
import * as Icons from '@hose/eui-icons';
import { Table, Button, Tooltip, Ellipsis } from '@hose/eui';
import { ToolComponentCtx } from '@hose/chat-app';
import { usePostmate, useConfig } from "../../hooks/useChatInitialization";
import { sendFillFormResult, sendPreviewAttachmentOrInvoice } from "../../utils/postmate";
import specificationIcons from './specificationIcons';
import { FilledTipsInfo } from '@hose/eui-icons'
import { getParamFromUrl } from '../../utils/tools'
import { getFeeTypePath, getInvoiceTagColorByType, FeeType } from './utils'
import moment from 'moment'
import './index.less';
import { GetInvoiceStateText } from './InvoiceComponents'
import PreImg from './PreImg'
import IconTag from '../IconTag'
import FeedbackButtons from '../FeedbackButtons';
import RelatedItem from '../RelatedApplication/RelatedItem';


const getSpecificationIdWithOutVersion = (specificationId: string) => specificationId.split(":").slice(0, -1).join(":")


const invoiceTypeListMap = {
  exist: '已有发票',
  noExist: '无发票',
  wait: '待开发票',
  unify: '统一开票',
  noWrite: '无需填写',
  existConfirm: '已有发票(待确认)'
}
interface TableRecord {
  [key: string]: any;
}


export const FlowResult: React.FC<ToolComponentCtx> = (props) => {
  const { state } = props
  // 使用 postmate 通信 hook
  const { messenger, isReady, isEmbedded } = usePostmate();
  // 获取父应用传递的配置数据
  const config = useConfig();
  const { specifications, feeTypesData, expenseLinkList } = config;
  // 生成表格列配置
  const generateColumns = (dataArray: any[]) => {
    if (!Array.isArray(dataArray) || dataArray.length === 0) return [];

    // 获取所有可能的字段
    const allKeys = new Set<string>();
    dataArray.forEach(item => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    return Array.from(allKeys).map(key => ({
      title: key,
      dataIndex: key,
      key: key,
      render: (value: any) => {
        return <span className="file-AI-result-content-value">
          {formatValue(value)}
        </span>
      }
    })).filter(item => item.dataIndex);
  };

  // 判断是否为简单对象（只有name和id字段）
  const isSimpleNameObject = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    const keys = Object.keys(obj);
    return keys.length <= 2 && (keys.includes('name') || keys.includes('id'));
  };

  // 判断是否为费用明细
  const isExpenseDetail = (key: string, value: any): boolean => {
    const expenseKeywords = ['费用明细', '申请明细', '报账明细', '付款明细', '结算明细',];
    return expenseKeywords.some(keyword => key.includes(keyword)) && Array.isArray(value);
  };

  // 检查是否为日期/时间范围对象
  const isRangeObject = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    const keys = Object.keys(obj);
    return keys.length === 2 &&
      ((keys.includes('start') && keys.includes('end')) ||
        (keys.includes('startTime') && keys.includes('endTime')) ||
        (keys.includes('startDate') && keys.includes('endDate')));
  };

  // 格式化范围对象为字符串
  const formatRangeObject = (obj: any): string => {
    const keys = Object.keys(obj);
    const startKey = keys.find(key => key.toLowerCase().includes('start'));
    const endKey = keys.find(key => key.toLowerCase().includes('end'));

    if (startKey && endKey) {
      const startValue = obj[startKey];
      const endValue = obj[endKey];
      return startValue && endValue ? `${startValue} ~ ${endValue}` : '无内容';
    }
    return '';
  };

  // 尝试解析JSON字符串并提取label
  const parseJsonString = (str: string): string => {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return parsed.map(item => {
          if (item && typeof item === 'object') {
            return item.label;
          }
          return item;
        }).join(', ');
      } else if (parsed && typeof parsed === 'object' && parsed.label) {
        return parsed.label;
      }
      return str;
    } catch {
      return str;
    }
  };

  // 智能格式化值
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '无内容';

    // 处理空数组
    if (Array.isArray(value)) {
      value = value.filter(item => !(item && typeof item === 'object' && Object.keys(item).length === 0))?.map(item => item?.name)?.filter(Boolean)
      if (value.length === 0) return '无内容';
      return value.join(',')
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // 处理空对象
      if (Object.keys(value).length === 0) return '无内容'
      // 处理发票
      if (!value?.name && value?.type && invoiceTypeListMap[value?.type as keyof typeof invoiceTypeListMap]) {
        return invoiceTypeListMap[value?.type as keyof typeof invoiceTypeListMap]
      }
      return value?.name
    }

    // 处理字符串类型
    if (typeof value === 'string') {
      // 空字符串处理
      if (value.trim() === '') return '无内容';

      // 尝试解析JSON字符串
      if ((value.startsWith('[') && value.endsWith(']')) ||
        (value.startsWith('{') && value.endsWith('}'))) {
        const parsed = parseJsonString(value) || '无内容';
        if (parsed !== value) return parsed;
      }
      return value;
    }

    // 处理范围对象
    if (isRangeObject(value)) {
      return formatRangeObject(value);
    }

    return String(value);
  };

  const getFeetypeName = (feeType?: FeeType) => {
    if (!feeType) return ''
    const fullname = getFeeTypePath(feeTypesData?.data, feeType)
    return fullname || feeType?.name
  }

  // 渲染费用明细
  const renderExpenseDetail = (feeTypesData: any, data: any[]) => {
    return <div className='feeDetails-container'>
      {data.map((item, index) => {
        const curFeeType = feeTypesData?.map?.[item.feeTypeId]
        const invoiceForm = item.feeTypeForm?.invoiceForm || {}
        let invoiceFormType = ''
        if (invoiceForm && (invoiceForm.type !== 'noWrite' || invoiceForm.invoiceDetail)) {
          if (invoiceForm.type === 'exist') {
            invoiceFormType = invoiceForm.invoiceConfirm
              ? invoiceForm.invoiceConfirm === 'false'
                ? 'existConfirm'
                : invoiceForm.type
              : invoiceForm.type
          } else {
            invoiceFormType = invoiceForm.type
          }
        }
        const feetypeName = getFeetypeName(feeTypesData?.map?.[item.feeTypeId])
        let code = feeTypesData?.map?.[item.feeTypeId]?.code
        if (code) code = '(' + code + ')'

        let attachments: { fileId: string, fileName: string }[] = []
        if (typeof item.feeTypeForm === 'object' && item.feeTypeForm !== null) {
          Object.keys(item.feeTypeForm).forEach(key => {
            const val = item.feeTypeForm[key]
            if (Array.isArray(val) && val.every(item => item.fileId && item.fileName)) {
              attachments = [...attachments, ...val]
            }
          })
        }


        return <ul key={index} className='feeDetails-item'>
          <li>
            <div className='item-title'>
              <div className="item-icon" style={{ background: curFeeType?.color }}>
                <img width={12} height={12} src={curFeeType?.icon} alt="" />
              </div>
              <span>{feetypeName}</span>
              <span className="item-code">{code}</span>
            </div>
            <div className='item-amount'>{item.feeTypeForm?.amount?.standardStrCode}&nbsp;{item.feeTypeForm?.amount?.standard}</div>
          </li>
          <li>消费时间：{item.feeTypeForm?.feeDate ? moment(item.feeTypeForm?.feeDate).format("YYYY-MM-DD") : '无'}</li>
          <li>消费事由：{item.feeTypeForm?.consumptionReasons || '无'}</li>
          <li className="invoices-or-attachments">
            {invoiceFormType && <IconTag onClick={() => {
              if (invoiceForm.attachments?.length > 0 || invoiceForm.invoices?.length > 0) {
                viewAttachment({
                  dataSource: item,
                  type: 'invoices',
                  title: '发票'
                })
              }
            }} color={getInvoiceTagColorByType(invoiceFormType)} text={<GetInvoiceStateText invoiceFormState={invoiceFormType} invoiceForm={invoiceForm} />} />}
            {
              attachments.length > 0 && <IconTag
                color="success"
                text={`附件*${attachments.length}`}
                onClick={() => viewAttachment({
                  dataSource: attachments,
                  type: 'attachments',
                  title: '附件'
                })}
              />}
          </li>
        </ul>
      })}
    </div>
  };

  const viewAttachment = async (data: any) => {
    try {
      // 使用工具函数发送填单数据
      await sendPreviewAttachmentOrInvoice(data);
    } catch (error) {
      console.error("❌ 发送过程中出现异常:", error);
      alert("发送过程中出现错误，请重试");
    }

  }

  /**
   * 一键填单函数
   */
  const handleQuickFill = async (data: any) => {
    console.log("触发一键填单功能");
    console.log("填单数据:", data);

    if (!isEmbedded) {
      console.log("⚠️ 非嵌入模式，无法发送数据到父页面");
      alert("当前不在嵌入模式下，无法发送数据到父页面");
      return;
    }

    if (!isReady) {
      console.log("⚠️ Postmate 未就绪，无法发送数据");
      alert("通信连接未建立，请稍后再试");
      return;
    }

    if (!messenger) {
      console.log("⚠️ Messenger 未初始化，无法发送数据");
      alert("通信组件未初始化，请刷新页面重试");
      return;
    }

    try {
      // 使用工具函数发送填单数据
      await sendFillFormResult(data);
    } catch (error) {
      console.error("❌ 发送过程中出现异常:", error);
      alert("发送过程中出现错误，请重试");
    }

    console.log("✅ 填单数据处理完成");
  };

  const AIResultDatas = useMemo(() => {
    if (props.state === 'result') {
      if (typeof props?.result === 'string') {
        try {
          return JSON.parse(props?.result)
        } catch {
          return props?.result
        }
      } else {
        return props?.result || []
      }
    }
  }, [props])

  const finalData = useMemo(() => {
    if (Array.isArray(AIResultDatas)) {
      return AIResultDatas.map((dataItem: Record<string, any>) => {
        let data: any = {}
        Object.keys(dataItem || {}).forEach((key: any) => {
          const item = dataItem[key]
          // 非法的日期范围值直接废弃了
          if (isRangeObject(item) && !formatRangeObject(item)?.includes('~')) {
            return
          }
          if (!(typeof item === 'object' && ['null', '[]', '{}'].includes(JSON.stringify(item)))) {
            data[key] = item
          }
        })
        return data
      })
    }

    return AIResultDatas

  }, [AIResultDatas])

  if (state !== 'result') {
    return null
    // return <ToolLoading speed='slow' />
  }

  if (typeof finalData === 'string') {
    return <span>{finalData}</span>
  }

  return <div className="flow-result-container">
    <div className='tip-text'>根据您提供的信息，提取出的主要内容如下：</div>
    {
      AIResultDatas.map((AIResultData: any, index: number) => {
        let isOldVersion = false
        let curSpecification = specifications && specifications.find((it) => it.id === AIResultData.specificationId)
        if (!curSpecification) {
          curSpecification = specifications && specifications.find((it) => getSpecificationIdWithOutVersion(it.id) === getSpecificationIdWithOutVersion(AIResultData.specificationId))
          curSpecification && (isOldVersion = true)
        }
        const disabled = (isEmbedded && (!isReady || !messenger)) || !curSpecification
        return <div className="file-AI-result-content-wrapper" key={index}>
          <div className="file-AI-result-content-header">
            <div className="file-AI-result-content-title-text" >
              <Icons.TwoToneGeneralAiSummary fontSize={16} />
              <span>AI 信息摘要</span>
            </div>
            {AIResultData.specificationId && <Tooltip title={!curSpecification && '无法使用该单据模板'}>
              <Button
                category="text"
                theme="highlight"
                size="small"
                className={`file-AI-result-content-btn ${disabled ? 'disabled-btn' : ''}`}
                onClick={() => handleQuickFill(finalData[index])}
                disabled={disabled}
                icon={<Icons.TwoToneGeneralAutofillStatic fontSize={14} />}
              >
                <span className='make-flow-btn'>一键填单</span>
              </Button>
            </Tooltip>}
          </div>
          <div className="result-wrapper">
            {
              Object.keys(AIResultData).map((key: string) => {
                const keyValue: any = AIResultData[key];
                // 特殊处理：specificationId
                if (key === 'specificationId') {
                  const IconCompo: any = specificationIcons(curSpecification?.icon)
                  return <div key={key} className="file-AI-result-content-item-dom specificationId-box">
                    <div className="file-AI-result-content-key">单据模板</div>
                    <div className="file-AI-result-content-value">
                      {curSpecification ? <div className="file-AI-result-content-template-item">
                        {IconCompo && <IconCompo fontSize={12} style={{ color: curSpecification?.color }} />}
                        <div className="file-AI-result-content-template-item-name">
                          {curSpecification?.name} {isOldVersion && <span className='old-version-text'>(旧版本)</span>}
                        </div>
                      </div> : <div className="file-AI-result-content-template-item">
                        <FilledTipsInfo fontSize={12} color="var(--eui-function-warning-500)" />
                        <div className="file-AI-result-content-template-item-name">
                          无权限
                        </div>
                      </div>}
                    </div>
                  </div>
                }
                // 特殊处理： 关联申请
                if (key === '关联申请') {
                  const ids = Array.isArray(keyValue) ? keyValue : [keyValue]
                  const list = Array.isArray(expenseLinkList) ? expenseLinkList.filter(item => ids.includes(item?.id)) : []
                  if (list?.length > 0) {
                    return <div key={key} className="file-AI-result-content-item-dom related-application-box">
                      <div className="file-AI-result-content-key">关联申请</div>
                      <div className="file-AI-result-content-value">
                        {
                          list.map((item: any) => (
                            <RelatedItem
                              key={item.id}
                              {...item}
                              className="related-content"
                            />
                          ))
                        }
                      </div>
                    </div>
                  }
                  return <div key={key} className="file-AI-result-content-item">
                    <span className="file-AI-result-content-key">{key}</span>
                    <span className="file-AI-result-content-value">无内容</span>
                  </div>
                }

                // 处理范围对象（start/end）
                if (isRangeObject(keyValue)) {
                  return <div key={key} className="file-AI-result-content-item">
                    <span className="file-AI-result-content-key">{key}</span>
                    <span className="file-AI-result-content-value">
                      {formatRangeObject(keyValue)}
                    </span>
                  </div>
                }

                // 处理简单对象（只有name, id等字段）
                if (isSimpleNameObject(keyValue)) {
                  return <div key={key} className="file-AI-result-content-item">
                    <span className="file-AI-result-content-key">{key}</span>
                    <span className="file-AI-result-content-value">
                      {keyValue.name || keyValue.id || Object.values(keyValue)[0] || '无内容'}
                    </span>
                  </div>
                }

                // 处理费用明细
                if (isExpenseDetail(key, keyValue)) {
                  if (keyValue.length > 0) {
                    return <div key={key} className="file-AI-result-content-item-dom">
                      <div className="file-AI-result-content-key">{key}</div>
                      <div className="file-AI-result-content-value">
                        {renderExpenseDetail(feeTypesData, keyValue)}
                      </div>
                    </div>
                  }
                }

                // 处理数组数据（表格展示）
                if (Array.isArray(keyValue)) {
                  // 空数组直接显示无内容
                  if (keyValue.length === 0) {
                    return <div key={key} className="file-AI-result-content-item">
                      <span className="file-AI-result-content-key">{key}</span>
                      <span className="file-AI-result-content-value">无内容</span>
                    </div>
                  }

                  if (keyValue.every((item: any) => item && typeof item !== 'object')) {
                    return <div key={key} className="file-AI-result-content-item">
                      <span className="file-AI-result-content-key">{key}</span>
                      <span className="file-AI-result-content-value">{keyValue.join(',')}</span>
                    </div>
                  }

                  const dataSource: TableRecord[] = keyValue.filter((item: any) =>
                    item && Object.values(item).filter(Boolean).length > 0
                  );

                  // 针对附件单独处理下
                  if (dataSource.every(item => item.fileName && item.fileId)) {
                    return <div key={key} className="file-AI-result-content-item-dom">
                      <div className="file-AI-result-content-key">{key}</div>
                      <div className="file-AI-result-content-value">
                        {
                          dataSource.map(file => {
                            return <Tooltip placement="top" title={file.fileName} key={file.key}>
                              <div className="file-AI-result-content__file-item" >
                                <PreImg file={file} />
                                <Ellipsis direction="middle" content={file.fileName} className="file-name" />
                              </div>
                            </Tooltip>
                          })
                        }
                      </div>
                    </div>
                  }

                  if (dataSource.every(item => isSimpleNameObject(item))) {
                    return <div key={key} className="file-AI-result-content-item">
                      <span className="file-AI-result-content-key">{key}</span>
                      <span className="file-AI-result-content-value">
                        {dataSource.map(item => item.name || item.id || Object.values(item)[0]).join(',') || '无内容'}
                      </span>
                    </div>
                  }


                  if (dataSource.length > 0) {
                    const columns = generateColumns(dataSource);
                    return <div key={key} className="file-AI-result-content-item-dom">
                      <div className="file-AI-result-content-key">{key}</div>
                      <div className="file-AI-result-content-value">
                        <Table
                          scroll={{
                            x: true
                          }}
                          onRow={() => {
                            return {
                              onWheel: (e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                // 查找表格的滚动容器
                                const target = e.currentTarget as HTMLElement;
                                const scrollContainer = target.closest('.eui-table-content')

                                if (scrollContainer) {
                                  // 检测是否为横向滚动意图（shift+滚轮 或 触摸板横向滑动）
                                  if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
                                    (scrollContainer as HTMLElement).scrollLeft += (e.deltaX || e.deltaY);
                                  }
                                }
                              }
                            }
                          }}
                          columns={columns}
                          dataSource={dataSource.map((item, index) => ({ ...item, key: index }))}
                          pagination={false}
                          bordered
                          size="small"
                          className='file-AI-result-table'
                        />
                      </div>
                    </div>
                  } else {
                    // 过滤后没有有效数据
                    return <div key={key} className="file-AI-result-content-item">
                      <span className="file-AI-result-content-key">{key}</span>
                      <span className="file-AI-result-content-value">无内容</span>
                    </div>
                  }
                }

                // 处理对象数据（多字段对象展示为表格）
                if (keyValue && typeof keyValue === 'object' && !Array.isArray(keyValue)) {
                  const objectKeys = Object.keys(keyValue);
                  if (objectKeys.length > 2) {
                    // 多字段对象转换为单行表格
                    const columns = objectKeys.map(objKey => ({
                      title: objKey,
                      dataIndex: objKey,
                      key: objKey,
                      render: (value: any) => {
                        return <span className="file-AI-result-content-value">
                          {formatValue(value)}
                        </span>
                      }
                    })).filter(item => item.dataIndex);

                    return <div key={key} className="file-AI-result-content-item-dom">
                      <div className="file-AI-result-content-key">{key}</div>
                      <div className="file-AI-result-content-value">
                        <Table
                          scroll={{
                            x: true
                          }}
                          columns={columns}
                          dataSource={[{ ...keyValue, key: 0 }]}
                          pagination={false}
                          bordered
                          size="small"
                          className='file-AI-result-table'
                          showHeader={true}
                        />
                      </div>
                    </div>
                  }
                }

                // 默认处理：简单值
                return <div key={key} className="file-AI-result-content-item">
                  <span className="file-AI-result-content-key">{key}</span>
                  <span className="file-AI-result-content-value">
                    {formatValue(keyValue)}
                  </span>
                </div>
              })
            }
            <div className="file-AI-result-content-footer">
              <FeedbackButtons
                feedbackData={{
                  chat_id: getParamFromUrl('sessionId') ?? undefined,
                  content: JSON.stringify(AIResultData),
                  // ai_chat: JSON.stringify(AIResultDatas) // TODO: 暂时无法获取对话内容
                }}
                className="ai-result-feedback"
              />
              <div className="file-AI-result-content-desc">内容由AI生成</div>
            </div>
          </div>
        </div>
      })
    }

    {AIResultDatas.length > 1 && <div className="max-width button-wrapper">
      <Button
        category="secondary"
        theme="highlight"
        size="small"
        block
        className="file-AI-result-content-btn"
        onClick={() => handleQuickFill(finalData.filter((item: any) => item.specificationId))}
        disabled={isEmbedded && (!isReady || !messenger)}
      >
        <span className='make-flow-btn'>全部填单</span>
      </Button>
    </div>}
  </div>
};

export default FlowResult;